import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators';

import { AreaDemographics, getDemographicsForCoordinates } from '../helpers/api/demographics-api';

@customElement('demographics-panel')
export class DemographicsPanel extends LitElement {
    amplitude_callsite: string = 'Demographics Panel';

    @property({ type: Number })
    lat: number | undefined;

    @property({ type: Number })
    lng: number | undefined;

    @state()
    private demographics: AreaDemographics | undefined;

    @state()
    private loading = false;

    @state()
    private failed = false;

    private loadedKey: string | undefined;

    protected updated() {
        if (this.lat === undefined || this.lng === undefined) {
            return;
        }
        const key = `${this.lat},${this.lng}`;
        if (this.loadedKey === key) {
            return;
        }
        this.loadedKey = key;
        void this.loadDemographics(this.lat, this.lng);
    }

    private async loadDemographics(lat: number, lng: number) {
        this.loading = true;
        this.failed = false;
        this.demographics = undefined;
        try {
            const result = await getDemographicsForCoordinates(this.amplitude_callsite, lat, lng);
            if (this.loadedKey === `${lat},${lng}`) {
                this.demographics = result;
            }
        } catch (error) {
            if (this.loadedKey === `${lat},${lng}`) {
                this.failed = true;
            }
        } finally {
            if (this.loadedKey === `${lat},${lng}`) {
                this.loading = false;
            }
        }
    }

    private formatPercent(percent: number): string {
        return percent >= 10 ? `${Math.round(percent)}%` : `${percent.toFixed(1)}%`;
    }

    protected render() {
        if (this.loading) {
            return html`<div class="demographics-panel__status">Loading area demographics…</div>`;
        }
        if (this.failed) {
            return html`<div class="demographics-panel__status">Area demographics aren't available for this location.</div>`;
        }
        if (this.demographics === undefined) {
            return html``;
        }
        const d = this.demographics;
        return html`
            <div class="demographics-panel">
                <div class="demographics-panel__header">Area Demographics — ${d.countyName}, ${d.stateCode}</div>
                <div class="demographics-panel__stats">
                    ${d.medianHouseholdIncome !== undefined ? html`
                        <div class="demographics-panel__stat">
                            <span class="demographics-panel__stat-value">$${d.medianHouseholdIncome.toLocaleString()}</span>
                            <span class="demographics-panel__stat-label">Median Household Income</span>
                        </div>
                    ` : ''}
                    ${d.medianAge !== undefined ? html`
                        <div class="demographics-panel__stat">
                            <span class="demographics-panel__stat-value">${d.medianAge.toFixed(1)}</span>
                            <span class="demographics-panel__stat-label">Median Age</span>
                        </div>
                    ` : ''}
                    <div class="demographics-panel__stat">
                        <span class="demographics-panel__stat-value">${Math.round(d.totalPopulation).toLocaleString()}</span>
                        <span class="demographics-panel__stat-label">Population</span>
                    </div>
                    ${d.genderShares.length > 0 ? html`
                        <div class="demographics-panel__stat">
                            <span class="demographics-panel__stat-value">${d.genderShares.map((share) => `${this.formatPercent(share.percent)} ${share.label}`).join(' · ')}</span>
                            <span class="demographics-panel__stat-label">Gender</span>
                        </div>
                    ` : ''}
                </div>
                <div class="demographics-panel__races">
                    ${d.raceShares.map((share) => html`
                        <div class="demographics-panel__race-row">
                            <span class="demographics-panel__race-label">${share.label}</span>
                            <div class="demographics-panel__race-bar-track">
                                <div class="demographics-panel__race-bar" style="width: ${Math.max(share.percent, 1)}%"></div>
                            </div>
                            <span class="demographics-panel__race-percent">${this.formatPercent(share.percent)}</span>
                        </div>
                    `)}
                </div>
                ${d.universities.length > 0 ? html`
                    <div class="demographics-panel__universities">
                        <div class="demographics-panel__section-title">Top Universities</div>
                        ${d.universities.map((university) => html`
                            <div class="demographics-panel__university-row">
                                <span class="demographics-panel__university-name">${university.name}</span>
                                <span class="demographics-panel__university-enrollment">${university.enrollment.toLocaleString()} students</span>
                            </div>
                        `)}
                    </div>
                ` : ''}
                <div class="demographics-panel__source">
                    Source: U.S. Census ACS 5-Year Estimates (${d.year}), county level ·
                    <a class="demographics-panel__source-link" href=${d.profileUrl} target="_blank" rel="noopener noreferrer">View Full Area Profile</a>
                </div>
            </div>
        `;
    }

    static get styles() {
        return css`
            .demographics-panel {
                border: 1px solid var(--theme-light-grey-color, #e0e0e0);
                border-radius: 8px;
                padding: 12px;
                margin: 8px 0;
                font-size: 13px;
            }

            .demographics-panel__status {
                font-size: 12px;
                color: var(--theme-grey-color, #757575);
                padding: 8px 0;
            }

            .demographics-panel__header {
                font-weight: 600;
                margin-bottom: 10px;
            }

            .demographics-panel__stats {
                display: flex;
                gap: 16px;
                margin-bottom: 12px;
                flex-wrap: wrap;
            }

            .demographics-panel__stat {
                display: flex;
                flex-direction: column;
            }

            .demographics-panel__stat-value {
                font-weight: 600;
                font-size: 15px;
            }

            .demographics-panel__stat-label {
                font-size: 11px;
                color: var(--theme-grey-color, #757575);
            }

            .demographics-panel__race-row {
                display: flex;
                align-items: center;
                gap: 8px;
                margin-bottom: 4px;
            }

            .demographics-panel__race-label {
                flex: 0 0 40%;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .demographics-panel__race-bar-track {
                flex: 1;
                height: 8px;
                background-color: var(--theme-light-grey-color, #eeeeee);
                border-radius: 4px;
                overflow: hidden;
            }

            .demographics-panel__race-bar {
                height: 100%;
                background-color: var(--main-featured-color, #4a7de2);
                border-radius: 4px;
            }

            .demographics-panel__race-percent {
                flex: 0 0 42px;
                text-align: right;
                font-variant-numeric: tabular-nums;
            }

            .demographics-panel__universities {
                margin-top: 12px;
            }

            .demographics-panel__section-title {
                font-weight: 600;
                margin-bottom: 6px;
            }

            .demographics-panel__university-row {
                display: flex;
                justify-content: space-between;
                gap: 8px;
                margin-bottom: 3px;
            }

            .demographics-panel__university-name {
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .demographics-panel__university-enrollment {
                flex: 0 0 auto;
                color: var(--theme-grey-color, #757575);
                font-variant-numeric: tabular-nums;
            }

            .demographics-panel__source {
                margin-top: 10px;
                font-size: 10px;
                color: var(--theme-grey-color, #9e9e9e);
            }

            .demographics-panel__source-link {
                color: inherit;
            }
        `;
    }
}
